/**
 * Guided Recording Container
 *
 * Main orchestrator for the guided recording flow:
 * 1. Request camera access
 * 2. Show recording session with pose detection
 * 3. Review and upload recorded video
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowBack } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { RecordingProvider, useRecording } from '../../../../context/RecordingContext';
import { RecordingActionType } from '../../../../context/RecordingContext';
import RecordingSession from './RecordingSession';
import PostRecordingReview from './PostRecordingReview';
import PreRecordingSetup from './components/PreRecordingSetup';
import { VideoRecorder, checkCameraAvailability } from '../../../../common/recordingUtilities';
import { debugLog } from '../../../../common/debugUtilities';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    minHeight: 400
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    gap: theme.spacing(2)
  },
  errorContainer: {
    padding: theme.spacing(3),
    maxWidth: 600,
    margin: '0 auto'
  },
  backButton: {
    marginBottom: theme.spacing(2)
  }
}));

type RecordingStep = 'setup' | 'initializing' | 'recording' | 'review';

interface GuidedRecordingContainerProps {
  infantDateOfBirth?: Date;
  onComplete: (videoFile: File) => void;
  onCancel: () => void;
}

function GuidedRecordingContent({
  infantDateOfBirth,
  onComplete,
  onCancel
}: GuidedRecordingContainerProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();
  const { state, dispatch } = useRecording();

  const [currentStep, setCurrentStep] = useState<RecordingStep>('setup');
  const [videoRecorder] = useState(() => new VideoRecorder());
  const [initError, setInitError] = useState<string | null>(null);

  // Initialize camera (triggered when user completes setup)
  const initializeCamera = useCallback(async () => {
    try {
      setCurrentStep('initializing');
      dispatch({ type: RecordingActionType.SET_INITIALIZING, payload: true });

      // Check camera availability
      const availability = await checkCameraAvailability();
      if (!availability.available) {
        throw new Error(availability.reason || t('errors.nocamera', {ns: 'guidedrecording'}));
      }

      // Request camera access
      const stream = await videoRecorder.startCamera();

      dispatch({ type: RecordingActionType.SET_CAMERA_STREAM, payload: stream });
      dispatch({ type: RecordingActionType.SET_CAMERA_PERMISSION, payload: true });
      dispatch({ type: RecordingActionType.SET_INITIALIZING, payload: false });
      setCurrentStep('recording');
    } catch (error) {
      console.error('[GuidedRecordingContainer] Camera initialization failed:', error);

      let errorMessage = t('errors.camerablocked', {ns: 'guidedrecording'});

      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          errorMessage = t('errors.camerablocked', {ns: 'guidedrecording'});
        } else if (error.message.includes('not found')) {
          errorMessage = t('errors.nocamera', {ns: 'guidedrecording'});
        } else if (error.message.includes('in use')) {
          errorMessage = t('errors.camerainuse', {ns: 'guidedrecording'});
        } else {
          errorMessage = error.message;
        }
      }

      setInitError(errorMessage);
      dispatch({ type: RecordingActionType.SET_CAMERA_ERROR, payload: errorMessage });
      dispatch({ type: RecordingActionType.SET_INITIALIZING, payload: false });
    }
  }, [dispatch, t, videoRecorder]);

  // Cleanup camera when component unmounts
  useEffect(() => {
    return () => {
      videoRecorder.stopCamera();
    };
  }, [videoRecorder]);

  // Handle recording completion
  const handleRecordingComplete = useCallback(
    (blob: Blob) => {
      debugLog('[GuidedRecordingContainer] Recording complete:', blob.size, 'bytes');
      setCurrentStep('review');
    },
    []
  );

  // Handle re-record
  const handleReRecord = useCallback(() => {
    dispatch({ type: RecordingActionType.SET_RECORDED_BLOB, payload: null });
    dispatch({ type: RecordingActionType.UPDATE_RECORDING_DURATION, payload: 0 });
    setCurrentStep('recording');
  }, [dispatch]);

  // Handle continue to upload
  const handleContinue = useCallback(
    (blob: Blob) => {
      // Convert blob to file
      const videoFile = videoRecorder.convertBlobToFile(blob, `cribsy-recording-${Date.now()}`);
      debugLog('[GuidedRecordingContainer] Video file created:', videoFile.name, videoFile.size);

      // Pass to parent component for upload
      onComplete(videoFile);
    },
    [videoRecorder, onComplete]
  );

  // Render based on current step
  if (initError) {
    return (
      <Box className={classes.errorContainer}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onCancel}
          className={classes.backButton}
        >
          Back
        </Button>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            {t('setup.camerafailed', {ns: 'guidedrecording'})}
          </Typography>
          <Typography variant="body2">{initError}</Typography>
        </Alert>
      </Box>
    );
  }

  if (currentStep === 'setup') {
    return (
      <PreRecordingSetup
        infantDateOfBirth={infantDateOfBirth}
        onContinue={initializeCamera}
        onCancel={onCancel}
      />
    );
  }

  if (currentStep === 'initializing' || state.isInitializing) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={60} />
        <Typography variant="h6">
          {t('setup.requestingcamera', {ns: 'guidedrecording'})}
        </Typography>
      </Box>
    );
  }

  if (currentStep === 'recording') {
    // Calculate infant age in months
    let infantAgeMonths: number | undefined;
    if (infantDateOfBirth) {
      const now = new Date();
      infantAgeMonths = (now.getFullYear() - infantDateOfBirth.getFullYear()) * 12
        + (now.getMonth() - infantDateOfBirth.getMonth());
      infantAgeMonths = Math.max(0, infantAgeMonths);
    }

    return (
      <RecordingSession
        infantAgeMonths={infantAgeMonths}
        onRecordingComplete={handleRecordingComplete}
        onCancel={onCancel}
      />
    );
  }

  if (currentStep === 'review' && state.recordedBlob) {
    return (
      <PostRecordingReview
        recordedBlob={state.recordedBlob}
        recordingDuration={state.recordingDuration}
        onReRecord={handleReRecord}
        onContinue={handleContinue}
      />
    );
  }

  return <></>;
}

// Main component with RecordingProvider
export default function GuidedRecordingContainer(props: GuidedRecordingContainerProps): JSX.Element {
  return (
    <RecordingProvider>
      <Box>
        <GuidedRecordingContent {...props} />
      </Box>
    </RecordingProvider>
  );
}
